import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface Player {
  id: string;
  nickname: string;
  sessionScore: number;
  uid: string; // Firebase UID
  email: string | null;
  photoURL: string | null;
}

interface PlayerContextType {
  player: Player | null;
  updateSessionScore: (points: number) => void;
  resetSessionScore: () => void;
  isAuthenticated: boolean;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}

interface PlayerProviderProps {
  children: ReactNode;
}

export function PlayerProvider({ children }: PlayerProviderProps) {
  const [player, setPlayerState] = useState<Player | null>(null);
  const { user, isAuthenticated: authIsAuthenticated } = useAuth();

  // Create player from Firebase auth user
  useEffect(() => {
    if (user && authIsAuthenticated) {
      // Try to load existing session score from localStorage
      const savedSessionScore = localStorage.getItem(`session-score-${user.uid}`);
      const sessionScore = savedSessionScore ? parseInt(savedSessionScore, 10) : 0;

      const newPlayer: Player = {
        id: user.uid, // Use Firebase UID as player ID
        nickname: user.displayName || user.email?.split('@')[0] || 'Player',
        sessionScore: sessionScore,
        uid: user.uid,
        email: user.email,
        photoURL: user.photoURL,
      };

      setPlayerState(newPlayer);
      console.log('🎮 Player created from Firebase user:', newPlayer);
    } else {
      // Clear player when user signs out
      setPlayerState(null);
    }
  }, [user, authIsAuthenticated]);

  // Listen for session reset events from SocketContext
  useEffect(() => {
    const handleSessionReset = () => {
      console.log('🔄 PlayerContext: Resetting session score due to session reset');
      console.log('🔄 Current player state before reset:', player);
      setPlayerState(prev => {
        if (prev) {
          const updatedPlayer = { ...prev, sessionScore: 0 };
          localStorage.setItem(`session-score-${prev.uid}`, '0');
          console.log('🔄 Player state after reset:', updatedPlayer);
          return updatedPlayer;
        }
        return null;
      });
    };

    // Listen for custom session reset event
    window.addEventListener('sessionReset', handleSessionReset);

    return () => {
      window.removeEventListener('sessionReset', handleSessionReset);
    };
  }, []);

  const updateSessionScore = useCallback((points: number) => {
    console.log('📊 Updating session score by:', points);
    setPlayerState(prev => {
      if (prev) {
        const updatedPlayer = { ...prev, sessionScore: prev.sessionScore + points };
        console.log('📊 Session score updated from', prev.sessionScore, 'to', updatedPlayer.sessionScore);
        localStorage.setItem(`session-score-${prev.uid}`, updatedPlayer.sessionScore.toString());
        return updatedPlayer;
      }
      return null;
    });
  }, []);

  const resetSessionScore = useCallback(() => {
    console.log('🔄 Manual session score reset called');
    setPlayerState(prev => {
      if (prev) {
        const updatedPlayer = { ...prev, sessionScore: 0 };
        localStorage.setItem(`session-score-${prev.uid}`, '0');
        console.log('🔄 Manual reset completed:', updatedPlayer);
        return updatedPlayer;
      }
      return null;
    });
  }, []);

  const isAuthenticated = authIsAuthenticated && player !== null;

  const value: PlayerContextType = {
    player,
    updateSessionScore,
    resetSessionScore,
    isAuthenticated,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
} 